// utils.ts
import { formatCurrencyAsText, toNumberOrZero } from '~/utils/functions';

export const toMoney2 = (n: number): number =>
  Number.isFinite(n) ? Number(n.toFixed(2)) : 0;

export const toMoney3 = (n: number): number =>
  Number.isFinite(n) ? Number(n.toFixed(3)) : 0;

export const formatCents = (cents: number) =>
  formatCurrencyAsText((cents / 100).toFixed(2));

export const formatBRNumber = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

export const formatCentsBR = (cents: number) => formatBRNumber(cents / 100);

// Se você quiser com "R$"
export const formatCentsBRL = (cents: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(cents / 100);

// Estratégia de totalização: alinhar com o RELATÓRIO (2 casas por item)
export type TotalizationStrategy = 'perItem2' | 'milsRound';
export const TOTALIZATION_STRATEGY: TotalizationStrategy = 'perItem2';

/** Total do item em centavos, ALINHADO ao relatório (2 casas por item) */
const itemTotalCentsPerItem2 = (val: number, qtd: number): number => {
  const v = toMoney2(toMoney3(val) * (toNumberOrZero(qtd) || 0)); // 2 casas por item
  return Math.round(v * 100); // centavos
};

/** Total alternativo (milésimos) — manter se precisar comparar */
const itemTotalCentsMilsRound = (val: number, qtd: number): number => {
  const unitMils = Math.round(toMoney3(val) * 1000);
  const qty = toNumberOrZero(qtd) || 0;
  return Math.round((unitMils * qty) / 10);
};

/** Ponte única para totalização */
export const itemTotalCents = (val: number, qtd: number): number =>
  TOTALIZATION_STRATEGY === 'perItem2'
    ? itemTotalCentsPerItem2(val, qtd)
    : itemTotalCentsMilsRound(val, qtd);

export const sumItensCents = (
  items: any[],
  key: 'val_icms_st' | 'val_ipi',
): number =>
  items.reduce((acc, it) => {
    const v = toMoney3(toNumberOrZero(it?.[key]));
    const q = toNumberOrZero(it?.qtd_entrada);
    return acc + itemTotalCents(v, q);
  }, 0);

/**
 * Mede o "passo efetivo": o MENOR número de passos de 0,001 no unitário
 * que gera delta de centavos ≠ 0 no total do item (conforme estratégia atual).
 */
export const measureEffectiveStep = (
  currentVal: number,
  qtd: number,
  direction: 1 | -1,
  maxUnitSteps = 1000,
): { unitSteps: number; deltaCents: number; nextVal: number } => {
  const start = itemTotalCents(currentVal, qtd);
  for (let k = 1; k <= maxUnitSteps; k++) {
    const nextVal = Math.max(0, toMoney3(currentVal + direction * k * 0.001));
    const delta = itemTotalCents(nextVal, qtd) - start;
    if ((direction === 1 && delta > 0) || (direction === -1 && delta < 0)) {
      return { unitSteps: k, deltaCents: delta, nextVal };
    }
    if (direction === -1 && nextVal === 0) break;
  }
  return { unitSteps: 0, deltaCents: 0, nextVal: currentVal };
};

/** GCD util (mantido) */
const gcd = (a: number, b: number): number => {
  let x = Math.abs(a),
    y = Math.abs(b);
  while (y !== 0) {
    const t = x % y;
    x = y;
    y = t;
  }
  return x;
};

export const gcdArray = (vals: number[]): number => {
  const nonZero = vals.filter((v) => v > 0);
  if (nonZero.length === 0) return 0;
  return nonZero.reduce((acc, v) => gcd(acc, v));
};

/**
 * Monta a lista de trabalho para ICMS-ST ou IPI.
 * - key === 'val_icms_st': inclui apenas itens com tipo_tributacao === 3 (ST) e qtd > 0.
 * - key === 'val_ipi': inclui itens com qtd > 0 (sem filtrar por ST).
 *
 * Observações:
 * - Usa toNumberOrZero para normalizar números.
 * - Usa toMoney3 para formatar o valor monetário.
 *
 * @param itens Lista de itens de entrada.
 * @param key Campo de valor a considerar: 'val_icms_st' | 'val_ipi'.
 * @returns Array com objetos { index, qtd, val } prontos para processamento.
 */
export const buildWorkList = (
  itens: any[],
  key: 'val_icms_st' | 'val_ipi',
): any[] =>
  (itens ?? [])
    .map((it: any, idx: number) => {
      const qtd = toNumberOrZero(it?.qtd_entrada);
      const val = toMoney3(toNumberOrZero(it?.[key]));

      // Para ICMS-ST, só seguimos se tipo_tributacao === 3; para IPI, ignoramos esse filtro.
      const tipo = Number(
        String(
          it?.tributacao?.tipo_tributacao ?? it?.tipo_tributacao ?? '',
        ).trim(),
      );
      const isST =
        key === 'val_icms_st' ? Number.isFinite(tipo) && tipo === 3 : true;

      return { index: idx, qtd, val, isST };
    })
    .filter(({ qtd, isST }) => qtd > 0 && isST)
    .map(({ index, val, qtd }) => ({ index, val, qtd }));
